import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Breadcrumbs,
  Link,
  Divider,
  Alert,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  TextField,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Speed as SpeedIcon,
  Psychology as PsychologyIcon,
  Memory as MemoryIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import evaluationService, {
  BaremosData,
  EvaluationInfo as ServiceEvaluationInfo,
  StroopCondition,
  FluenciasCondition,
  HVLTRCondition,
  ReyFigureCondition,
  D2Condition,
  ElevatorCondition,
  IFSChNormRow,
} from '../services/evaluationService';
import EvaluationSelectionPanel, { EvaluationSelectionData, classificationColors } from '../components/EvaluationSelectionPanel';
import { LoadingOrb } from '../components/LoadingOrb';
import { useAuth } from '../contexts/AuthContext';
import adminService from '../services/adminService';

interface LocalEvaluationInfo {
  id: string;
  name: string;
  fullName: string;
  description: string;
  category: string;
  icon: React.ReactNode;
}

const evaluations: Record<string, LocalEvaluationInfo> = {
  'tmt-a': {
    id: 'tmt-a',
    name: 'TMT-A',
    fullName: 'Trail Making Test - Parte A',
    description: 'Evaluación de velocidad de procesamiento y atención visual',
    category: 'Atención y Velocidad',
    icon: <SpeedIcon />
  },
  'tmt-b': {
    id: 'tmt-b',
    name: 'TMT-B',
    fullName: 'Trail Making Test - Parte B',
    description: 'Evaluación de flexibilidad cognitiva y función ejecutiva',
    category: 'Función Ejecutiva',
    icon: <PsychologyIcon />
  },
  'stroop': {
    id: 'stroop',
    name: 'Stroop',
    fullName: 'Test de Stroop',
    description: 'Evaluación de control inhibitorio y atención selectiva',
    category: 'Control Inhibitorio',
    icon: <MemoryIcon />
  },
  'fluencias': {
    id: 'fluencias',
    name: 'Fluencias',
    fullName: 'Test de Fluencias',
    description: 'Evaluación de fluencia verbal semántica (animales) y fonológica (letras F, A, S)',
    category: 'Lenguaje',
    icon: <PsychologyIcon />
  },
  'hvlt-r': {
    id: 'hvlt-r',
    name: 'HVLT-R',
    fullName: 'Hopkins Verbal Learning Test - Revised',
    description: 'Evaluación de memoria verbal y aprendizaje. Incluye Recuerdo Total y Recuerdo Tardío.',
    category: 'Memoria',
    icon: <MemoryIcon />
  },
  'rey-figure': {
    id: 'rey-figure',
    name: 'Figura Compleja de Rey',
    fullName: 'Test de Figura Compleja de Rey-Osterrieth',
    description: 'Evaluación de la función visuoespacial y memoria visual mediante la copia y reproducción de memoria de una figura compleja',
    category: 'Visuoespacial',
    icon: <PsychologyIcon />
  },
  d2: {
    id: 'd2',
    name: 'D2',
    fullName: 'Test de Atención D2',
    description: 'Baremos por métrica, percentil y tramo etario',
    category: 'Atención',
    icon: <SpeedIcon />
  },
  'elevator-appendix-3b': {
    id: 'elevator-appendix-3b',
    name: 'Conteo de Elevator 3B',
    fullName: 'Conteo de Elevator con Distracción - Apéndice 3B',
    description: 'Baremos percentilares aproximados por versión A/B y grupo etario (Apéndice 3A excluido)',
    category: 'Atención',
    icon: <SpeedIcon />
  },
  'ifs-ch': {
    id: 'ifs-ch',
    name: 'IFS-Ch',
    fullName: 'IFS-Ch (INECO Frontal Screening - Chile)',
    description: 'Media ± DE de sujetos control (n=30) por subtest. Tabla de referencia, sin percentiles ni grupos etarios/educacionales.',
    category: 'Función Ejecutiva',
    icon: <PsychologyIcon />
  },
  moca: {
    id: 'moca',
    name: 'MOCA',
    fullName: 'Evaluación Cognitiva de Montreal (MoCA)',
    description: 'Baremos del puntaje directo total de MoCA (0-30) por grupo etario',
    category: 'Cribado Cognitivo Global',
    icon: <PsychologyIcon />
  },
  'boston-naming': {
    id: 'boston-naming',
    name: 'Boston Naming Test',
    fullName: 'Boston Naming Test (BNT)',
    description: 'Datos normativos del BNT estratificados por edad y nivel educacional',
    category: 'Lenguaje',
    icon: <PsychologyIcon />
  }
};

// Componente para renderizar una tabla de baremos
interface BaremosTableProps {
  baremosData: Record<string, BaremosData[]>;
  units: string;
  canBeNegative?: boolean;
  tableKey?: string;
  isAdmin?: boolean;
  onRefresh?: () => void;
  /** Nombre real de la columna de puntaje en la tabla (algunos baremos, ej. BNT, no usan la convención "score_value"). */
  scoreFieldKey?: string;
}

// Componente específico para baremos de Rey Figure (solo edad, sin educación)
interface ReyFigureBaremosTableProps {
  baremosData: Record<string, BaremosData[]>;
  units: string;
  isStringValue?: boolean; // Para valores string como números romanos (I, II, III, etc.)
  tableKey?: string;
  isAdmin?: boolean;
  onRefresh?: () => void;
}

interface D2BaremosTableProps {
  baremosData: Record<string, BaremosData[]>;
  tableKey?: string;
  isAdmin?: boolean;
  onRefresh?: () => void;
}

type D2ViewMode = 'metric' | 'age';

const ElevatorReferenceGuide: React.FC = () => {
  const subtest2Columns = [1, 2, 3, 4, 5, 6, 7];
  const subtest2Rows = [
    { tone: 'A', values: [3, 5, 6, 8, 11, 9, 14] },
    { tone: 'B', values: [4, 6, 7, 9, 12, 14, 10] },
    { tone: 'C', values: [5, 7, 6, 8, 10, 14, 12] },
  ];

  const subtest3Columns = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const subtest3Rows = [
    { tone: 'A', values: [2, 4, 6, 8, 7, 10, 9, 12, 11, 14] },
    { tone: 'B', values: [3, 5, 6, 7, 8, 11, 10, 14, 12, 9] },
    { tone: 'C', values: [4, 3, 7, 6, 8, 12, 11, 9, 10, 14] },
  ];

  const renderGuideTable = (
    title: string,
    columns: number[],
    rows: Array<{ tone: string; values: number[] }>,
    maxScore: number,
  ) => (
    <Box sx={{ flex: 1, minWidth: 320 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: 64 }}>
                Tono
              </TableCell>
              {columns.map((col) => (
                <TableCell key={col} sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.tone}>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                  {row.tone}
                </TableCell>
                {row.values.map((value, idx) => (
                  <TableCell key={`${row.tone}-${idx}`} sx={{ textAlign: 'center' }}>
                    {value}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Raw score: total de respuestas correctas (máximo = {maxScore}).
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        mt: 3,
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'),
      }}
    >
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Tabla referencial de aplicación
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Guía rápida para administrar la prueba Elevator (referencia visual; no reemplaza el manual).
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {renderGuideTable('Subprueba 2: Conteo de Elevator', subtest2Columns, subtest2Rows, 7)}
        {renderGuideTable('Subprueba 3: Conteo de Elevator con Distracción', subtest3Columns, subtest3Rows, 10)}
      </Box>
    </Box>
  );
};

// Tipo para la celda seleccionada/hover
interface CellPosition {
  percentile: number;
  age: string;
  education: string;
  scoreValue?: number | null;
  rowId?: number;
}

const BaremosTable: React.FC<BaremosTableProps> = ({ baremosData, units, canBeNegative = false, tableKey, isAdmin = false, onRefresh, scoreFieldKey = 'score_value' }) => {
  const [hoveredCell, setHoveredCell] = useState<CellPosition | null>(null);
  const [selectedCell, setSelectedCell] = useState<CellPosition | null>(null);
  const [classifications, setClassifications] = useState<string[]>([]);
  const [conversions, setConversions] = useState<any[]>([]);

  const percentiles = [95, 90, 85, 80, 70, 60, 50, 40, 30, 20, 15, 10, 5];
  const ageGroups = ['18-22', '23-27', '28-32', '33-37', '38-42', '43-47', '48-52', '53-57', '58-62', '63-67', '68-72', '73-77', '>77'];
  const educationLevels = Object.keys(baremosData);

  const getScoreValue = (education: string, age: string, percentile: number): number | null => {
    const baremos = baremosData[education];
    if (!baremos) return null;

    const baremo = baremos.find(b => b.age_group === age && b.percentile === percentile);
    const scoreValue = (baremo as any)?.[scoreFieldKey];

    if (scoreValue !== null && scoreValue !== undefined && !isNaN(Number(scoreValue))) {
      return Number(scoreValue);
    }
    
    return null;
  };

  // Obtener el baremo completo para una celda específica
  const getBaremoForCell = (education: string, age: string, percentile: number) => {
    const baremos = baremosData[education];
    if (!baremos) return null;
    return baremos.find(b => b.age_group === age && b.percentile === percentile) || null;
  };

  // Manejar clic para selección persistente
  const handleCellClick = async (percentile: number, age: string, education: string, scoreValue: number | null) => {
    // Si se hace clic en la misma celda, deseleccionar
    if (selectedCell?.percentile === percentile && selectedCell?.age === age && selectedCell?.education === education) {
      setSelectedCell(null);
      setClassifications([]);
      setConversions([]);
    } else {
      // Obtener el baremo real para usar su percentil real (puede ser diferente del de la fila)
      const baremo = getBaremoForCell(education, age, percentile);
      const actualPercentile = baremo?.percentile !== undefined && baremo?.percentile !== null
        ? Number(baremo.percentile)
        : percentile;
      setSelectedCell({ percentile, age, education, scoreValue, rowId: baremo?.id });
      
      // Obtener conversiones y clasificaciones del percentil (puede ser múltiple si está entre rangos)
      try {
        // Obtener clasificaciones (esta función maneja rangos adyacentes)
        const classificationsResult = await evaluationService.getClassificationsByPercentile(actualPercentile);
        setClassifications(classificationsResult);
        
        // Obtener conversiones (puede ser múltiple si está entre rangos con diferentes clasificaciones)
        const conversionsResult = await evaluationService.getConversionsByPercentile(actualPercentile);
        setConversions(conversionsResult);
      } catch (error) {
        console.error('Error obteniendo conversiones:', error);
        setConversions([]);
        setClassifications([]);
      }
    }
  };

  // Limpiar selección al hacer clic fuera de la tabla
  const handleTableContainerClick = (e: React.MouseEvent) => {
    // Solo limpiar si el clic fue directamente en el container, no en las celdas
    if (e.target === e.currentTarget) {
      setSelectedCell(null);
      setClassifications([]);
      setConversions([]);
    }
  };

  return (
    <>
      {educationLevels.map((education, eduIndex) => (
        <Box key={education} sx={{ mb: 4 }}>
          {/* Título del nivel educativo */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'primary.main',
                fontWeight: 'bold',
                textAlign: 'center',
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                py: 1,
                px: 2,
                borderRadius: 1,
                flex: 1
              }}
            >
              {education}
            </Typography>
          </Box>

          {/* Tabla de baremos */}
          <TableContainer component={Paper} sx={{ mb: 2 }} onClick={handleTableContainerClick}>
            <Table size="small">
              <TableHead>
                {/* Fila superior con "Edad" */}
                <TableRow>
                  <TableCell 
                    sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200',
                      minWidth: 80,
                      borderBottom: 0
                    }}
                  >
                  </TableCell>
                  <TableCell 
                    colSpan={ageGroups.length}
                    sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200',
                      textAlign: 'center',
                      fontSize: '0.9rem'
                    }}
                  >
                    Edad (años)
                  </TableCell>
                </TableRow>
                {/* Fila con percentiles y grupos de edad */}
                <TableRow>
                  <TableCell 
                    sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200',
                      minWidth: 80,
                      textAlign: 'center'
                    }}
                  >
                    Percentile
                  </TableCell>
                  {ageGroups.map(age => {
                    const isHoveredColumn = hoveredCell?.age === age && hoveredCell?.education === education;
                    const isSelectedColumn = selectedCell?.age === age && selectedCell?.education === education;
                    return (
                      <TableCell 
                        key={age}
                        sx={{ 
                          fontWeight: 'bold', 
                          backgroundColor: isSelectedColumn 
                            ? 'rgba(76, 175, 80, 0.25)' 
                            : isHoveredColumn 
                              ? 'rgba(25, 118, 210, 0.15)' 
                              : (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200',
                          textAlign: 'center',
                          minWidth: 60,
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        {age}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {percentiles.map(percentile => {
                  const isHoveredRow = hoveredCell?.percentile === percentile && hoveredCell?.education === education;
                  const isSelectedRow = selectedCell?.percentile === percentile && selectedCell?.education === education;
                  
                  return (
                    <TableRow 
                      key={percentile} 
                      sx={{
                        backgroundColor: isSelectedRow 
                          ? 'rgba(76, 175, 80, 0.08)' 
                          : isHoveredRow 
                            ? 'rgba(25, 118, 210, 0.08)' 
                            : 'transparent',
                        transition: 'background-color 0.2s ease'
                      }}
                    >
                      <TableCell 
                        sx={{ 
                          fontWeight: 'bold',
                          backgroundColor: isSelectedRow 
                            ? 'rgba(76, 175, 80, 0.25)' 
                            : isHoveredRow 
                              ? 'rgba(25, 118, 210, 0.15)' 
                              : (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                          borderRight: 1,
                          borderColor: (theme) => theme.palette.mode === 'dark' ? 'grey.600' : 'grey.300',
                          transition: 'background-color 0.2s ease',
                          textAlign: 'center'
                        }}
                      >
                        {percentile}
                      </TableCell>
                      {ageGroups.map(age => {
                        const scoreValue = getScoreValue(education, age, percentile);
                        const isHovered = (hoveredCell?.percentile === percentile || hoveredCell?.age === age) && hoveredCell?.education === education;
                        const isSelected = (selectedCell?.percentile === percentile || selectedCell?.age === age) && selectedCell?.education === education;
                        const isExactSelected = selectedCell?.percentile === percentile && selectedCell?.age === age && selectedCell?.education === education;
                        const isExactHovered = hoveredCell?.percentile === percentile && hoveredCell?.age === age && hoveredCell?.education === education;
                        
                        // Determinar el color de fondo
                        let backgroundColor = 'transparent';
                        if (isExactSelected) {
                          backgroundColor = 'rgba(76, 175, 80, 0.4)'; // Verde fuerte para la celda exacta seleccionada
                        } else if (isExactHovered) {
                          backgroundColor = 'rgba(25, 118, 210, 0.25)'; // Azul fuerte para la celda exacta hover
                        } else if (isSelected && isHovered) {
                          backgroundColor = 'rgba(76, 175, 80, 0.2)'; // Verde medio cuando está en fila/columna seleccionada + hover
                        } else if (isSelected) {
                          backgroundColor = 'rgba(76, 175, 80, 0.12)'; // Verde claro para fila/columna seleccionada
                        } else if (isHovered) {
                          backgroundColor = 'rgba(25, 118, 210, 0.12)'; // Azul claro para hover
                        }
                        
                        return (
                          <TableCell 
                            key={age}
                            onClick={() => handleCellClick(percentile, age, education, scoreValue)}
                            sx={{ 
                              textAlign: 'center',
                              fontWeight: scoreValue !== null ? 'bold' : 'normal',
                              color: scoreValue !== null 
                                ? (canBeNegative && scoreValue < 0 ? 'error.main' : 'text.primary') 
                                : 'text.secondary',
                              backgroundColor,
                              cursor: 'pointer',
                              transition: 'background-color 0.15s ease',
                              border: isExactSelected ? '2px solid' : 'none',
                              borderColor: isExactSelected ? 'success.main' : 'transparent',
                              '&:hover': {
                                backgroundColor: isExactSelected 
                                  ? 'rgba(76, 175, 80, 0.5)' 
                                  : 'rgba(25, 118, 210, 0.25)'
                              }
                            }}
                            onMouseEnter={() => setHoveredCell({ percentile, age, education, scoreValue })}
                            onMouseLeave={() => setHoveredCell(null)}
                          >
                            {scoreValue !== null && typeof scoreValue === 'number' ? scoreValue.toFixed(1) : '-'}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <EvaluationSelectionPanel
            key={selectedCell && selectedCell.education === education ? `${education}-${selectedCell.age}-${selectedCell.percentile}` : 'none'}
            selection={
              selectedCell && selectedCell.education === education
                ? {
                    label: `Edad ${selectedCell.age}`,
                    scoreValue: selectedCell.scoreValue ?? null,
                    units,
                    percentile: selectedCell.percentile,
                    classifications,
                    conversions,
                  }
                : null
            }
            editable={isAdmin && !!tableKey && selectedCell?.education === education && !!selectedCell?.rowId}
            editableFields={
              selectedCell?.education === education && selectedCell?.rowId
                ? [
                    { key: 'percentile', label: 'Percentil', value: selectedCell.percentile, type: 'number' },
                    { key: scoreFieldKey, label: `Puntaje (${units})`, value: selectedCell.scoreValue ?? null, type: 'number' },
                  ]
                : undefined
            }
            onSaveEdit={
              selectedCell?.education === education && selectedCell?.rowId && tableKey
                ? async (values) => {
                    await adminService.updateNormRow(tableKey, selectedCell.rowId as number, values);
                    setSelectedCell(null);
                    setClassifications([]);
                    setConversions([]);
                    onRefresh?.();
                  }
                : undefined
            }
          />

          {/* Separador entre niveles educativos */}
          {eduIndex < educationLevels.length - 1 && (
            <Divider sx={{ my: 3 }} />
          )}
        </Box>
      ))}

      {/* Nota informativa */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.15)' : 'info.50', borderRadius: 1 }}>
        <Typography variant="body2" color="info.main">
          <strong>Nota:</strong> Los valores representan {units}. 
          {canBeNegative 
            ? ' Los valores negativos (en rojo) indican menor resistencia a la interferencia.' 
            : ' Un valor más alto indica mejor rendimiento.'
          }
          {' '}Los datos están basados en estudios normativos de la población chilena.
          {' '}<strong>Tip:</strong> Haz clic en una celda para mantener la selección resaltada.
        </Typography>
      </Box>
    </>
  );
};

// Componente específico para baremos de Rey Figure (solo edad, sin educación)
const ReyFigureBaremosTable: React.FC<ReyFigureBaremosTableProps> = ({ baremosData, units, isStringValue = false, tableKey, isAdmin = false, onRefresh }) => {
  const [hoveredCell, setHoveredCell] = useState<{ percentile: number; age: string; scoreValue?: number | string | null } | null>(null);
  const [selectedCell, setSelectedCell] = useState<{ percentile: number; age: string; scoreValue?: number | string | null; rowId?: number } | null>(null);
  const [classifications, setClassifications] = useState<string[]>([]);
  const [conversions, setConversions] = useState<any[]>([]);

  // Para Rey Figure: edades específicas y percentiles específicos
  // Las edades vienen de los datos (keys del objeto baremosData)
  const ageGroups = Object.keys(baremosData).sort((a, b) => {
    // Ordenar: 5, 6, 7, ..., 14, 15+
    if (a === '15+') return 1;
    if (b === '15+') return -1;
    return parseInt(a) - parseInt(b);
  });

  // Obtener todos los percentiles únicos de los datos
  const allPercentiles = new Set<number>();
  Object.values(baremosData).forEach(baremos => {
    baremos.forEach(b => {
      if (b.percentile !== null && b.percentile !== undefined) {
        allPercentiles.add(Number(b.percentile));
      }
    });
  });
  
  // Ordenar percentiles de mayor a menor
  const percentiles = Array.from(allPercentiles).sort((a, b) => b - a);

  const getScoreValue = (age: string, percentile: number): number | string | null => {
    const baremos = baremosData[age];
    if (!baremos) return null;
    
    const baremo = baremos.find(b => b.percentile === percentile);
    const scoreValue = baremo?.score_value;
    
    if (scoreValue === null || scoreValue === undefined) {
      return null;
    }
    
    // Si es string value (números romanos), retornar el string
    if (isStringValue && typeof scoreValue === 'string') {
      return scoreValue;
    }
    
    // Si es numérico, retornar número
    if (!isNaN(Number(scoreValue))) {
      return Number(scoreValue);
    }
    
    return null;
  };

  const getBaremoForCell = (age: string, percentile: number) => {
    const baremos = baremosData[age];
    if (!baremos) return null;
    return baremos.find(b => b.percentile === percentile) || null;
  };

  const handleCellClick = async (percentile: number, age: string, scoreValue: number | string | null) => {
    // Si la celda no tiene valor (NULL), no hacer nada o deseleccionar
    if (scoreValue === null || scoreValue === undefined) {
      if (selectedCell?.percentile === percentile && selectedCell?.age === age) {
        setSelectedCell(null);
        setClassifications([]);
        setConversions([]);
      }
      return; // No procesar celdas sin valor
    }

    // Para valores string (números romanos), no mostrar conversiones
    if (isStringValue && typeof scoreValue === 'string') {
      if (selectedCell?.percentile === percentile && selectedCell?.age === age) {
        setSelectedCell(null);
        setClassifications([]);
        setConversions([]);
      } else {
        const stringBaremo = getBaremoForCell(age, percentile);
        setSelectedCell({ percentile, age, scoreValue, rowId: stringBaremo?.id });
        setClassifications([]);
        setConversions([]);
      }
      return;
    }

    if (selectedCell?.percentile === percentile && selectedCell?.age === age) {
      setSelectedCell(null);
      setClassifications([]);
      setConversions([]);
    } else {
      const baremo = getBaremoForCell(age, percentile);
      const actualPercentile = baremo?.percentile !== undefined && baremo?.percentile !== null
        ? Number(baremo.percentile)
        : percentile;
      setSelectedCell({ percentile, age, scoreValue, rowId: baremo?.id });
      
      try {
        const classificationsResult = await evaluationService.getClassificationsByPercentile(actualPercentile);
        setClassifications(classificationsResult);
        
        const conversionsResult = await evaluationService.getConversionsByPercentile(actualPercentile);
        setConversions(conversionsResult);
      } catch (error) {
        console.error('Error obteniendo conversiones:', error);
        setConversions([]);
        setClassifications([]);
      }
    }
  };

  const handleTableContainerClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setSelectedCell(null);
      setClassifications([]);
      setConversions([]);
    }
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ mb: 2 }} onClick={handleTableContainerClick}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ 
                  fontWeight: 'bold', 
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200',
                  minWidth: 80,
                  textAlign: 'center'
                }}
              >
                Percentil
              </TableCell>
              {ageGroups.map(age => {
                const isHoveredColumn = hoveredCell?.age === age;
                const isSelectedColumn = selectedCell?.age === age;
                return (
                  <TableCell 
                    key={age}
                    sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: isSelectedColumn 
                        ? 'rgba(76, 175, 80, 0.25)' 
                        : isHoveredColumn 
                          ? 'rgba(25, 118, 210, 0.15)' 
                          : (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200',
                      textAlign: 'center',
                      minWidth: 60,
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    {age === '15+' ? '15+' : `${age} años`}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {percentiles.map(percentile => {
              const isHoveredRow = hoveredCell?.percentile === percentile;
              const isSelectedRow = selectedCell?.percentile === percentile;
              
              return (
                <TableRow 
                  key={percentile} 
                  sx={{
                    backgroundColor: isSelectedRow 
                      ? 'rgba(76, 175, 80, 0.08)' 
                      : isHoveredRow 
                        ? 'rgba(25, 118, 210, 0.08)' 
                        : 'transparent',
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  <TableCell 
                    sx={{ 
                      fontWeight: 'bold',
                      backgroundColor: isSelectedRow 
                        ? 'rgba(76, 175, 80, 0.25)' 
                        : isHoveredRow 
                          ? 'rgba(25, 118, 210, 0.15)' 
                          : (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                      borderRight: 1,
                      borderColor: (theme) => theme.palette.mode === 'dark' ? 'grey.600' : 'grey.300',
                      transition: 'background-color 0.2s ease',
                      textAlign: 'center'
                    }}
                  >
                    {percentile}
                  </TableCell>
                  {ageGroups.map(age => {
                    const scoreValue = getScoreValue(age, percentile);
                    const hasValue = scoreValue !== null && scoreValue !== undefined;
                    const isString = isStringValue && typeof scoreValue === 'string';
                    const isHovered = hasValue && (hoveredCell?.percentile === percentile || hoveredCell?.age === age);
                    const isSelected = hasValue && (selectedCell?.percentile === percentile || selectedCell?.age === age);
                    const isExactSelected = hasValue && selectedCell?.percentile === percentile && selectedCell?.age === age;
                    const isExactHovered = hasValue && hoveredCell?.percentile === percentile && hoveredCell?.age === age;
                    
                    let backgroundColor = 'transparent';
                    if (isExactSelected) {
                      backgroundColor = 'rgba(76, 175, 80, 0.4)';
                    } else if (isExactHovered) {
                      backgroundColor = 'rgba(25, 118, 210, 0.25)';
                    } else if (isSelected && isHovered) {
                      backgroundColor = 'rgba(76, 175, 80, 0.2)';
                    } else if (isSelected) {
                      backgroundColor = 'rgba(76, 175, 80, 0.12)';
                    } else if (isHovered) {
                      backgroundColor = 'rgba(25, 118, 210, 0.12)';
                    }
                    
                    return (
                      <TableCell 
                        key={age}
                        onClick={() => hasValue && handleCellClick(percentile, age, scoreValue)}
                        sx={{ 
                          textAlign: 'center',
                          fontWeight: hasValue ? 'bold' : 'normal',
                          color: hasValue ? 'text.primary' : 'text.secondary',
                          backgroundColor,
                          cursor: hasValue ? 'pointer' : 'default',
                          transition: 'background-color 0.15s ease',
                          border: isExactSelected ? '2px solid' : 'none',
                          borderColor: isExactSelected ? 'success.main' : 'transparent',
                          opacity: hasValue ? 1 : 0.5,
                          '&:hover': {
                            backgroundColor: hasValue 
                              ? (isExactSelected 
                                  ? 'rgba(76, 175, 80, 0.5)' 
                                  : 'rgba(25, 118, 210, 0.25)')
                              : backgroundColor
                          }
                        }}
                        onMouseEnter={() => {
                          if (hasValue) {
                            setHoveredCell({ percentile, age, scoreValue });
                          }
                        }}
                        onMouseLeave={() => setHoveredCell(null)}
                      >
                        {hasValue 
                          ? (isString 
                              ? scoreValue.toString() 
                              : typeof scoreValue === 'number' 
                                ? (scoreValue % 1 === 0 ? scoreValue.toString() : scoreValue.toFixed(2))
                                : '-')
                          : '-'
                        }
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <EvaluationSelectionPanel
        key={selectedCell ? `${selectedCell.age}-${selectedCell.percentile}` : 'none'}
        selection={
          selectedCell
            ? {
                label: `Edad ${selectedCell.age === '15+' ? '15+' : `${selectedCell.age} años`}`,
                scoreValue: selectedCell.scoreValue ?? null,
                units,
                percentile: selectedCell.percentile,
                classifications,
                conversions,
              }
            : null
        }
        editable={isAdmin && !!tableKey && !!selectedCell?.rowId}
        editableFields={
          selectedCell?.rowId
            ? [
                { key: 'percentile', label: 'Percentil', value: selectedCell.percentile, type: 'number' },
                { key: 'score_value', label: `Puntaje (${units})`, value: selectedCell.scoreValue ?? null, type: isStringValue ? 'text' : 'number' },
              ]
            : undefined
        }
        onSaveEdit={
          selectedCell?.rowId && tableKey
            ? async (values) => {
                await adminService.updateNormRow(tableKey, selectedCell.rowId as number, values);
                setSelectedCell(null);
                setClassifications([]);
                setConversions([]);
                onRefresh?.();
              }
            : undefined
        }
      />

      {/* Nota informativa */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.15)' : 'info.50', borderRadius: 1 }}>
        <Typography variant="body2" color="info.main">
          <strong>Nota:</strong> Los valores representan {units}.
          Los datos están basados en estudios normativos de la población española.
          {' '}<strong>Tip:</strong> Haz clic en una celda para mantener la selección resaltada.
        </Typography>
      </Box>
    </>
  );
};

const D2BaremosTable: React.FC<D2BaremosTableProps> = ({ baremosData, tableKey, isAdmin = false, onRefresh }) => {
  const [hoveredCell, setHoveredCell] = useState<{ percentile: number; age: string; scoreValue?: string | null } | null>(null);
  const [selectedCell, setSelectedCell] = useState<{ percentile: number; age: string; scoreValue?: string | null; rowId?: number } | null>(null);
  const [classifications, setClassifications] = useState<string[]>([]);
  const [conversions, setConversions] = useState<any[]>([]);

  const ageGroups = Object.keys(baremosData).sort((a, b) => a.localeCompare(b, 'es'));
  const allPercentiles = new Set<number>();

  Object.values(baremosData).forEach((rows) => {
    rows.forEach((row) => {
      if (row.percentile !== null && row.percentile !== undefined) {
        allPercentiles.add(Number(row.percentile));
      }
    });
  });

  const percentiles = Array.from(allPercentiles).sort((a, b) => b - a);

  const getBaremoForCell = (ageGroup: string, percentile: number): BaremosData | null => {
    const rows = baremosData[ageGroup] || [];
    return rows.find((r) => Number(r.percentile) === percentile) || null;
  };

  const getScoreValue = (ageGroup: string, percentile: number): string => {
    const rows = baremosData[ageGroup] || [];
    const row = rows.find((r) => Number(r.percentile) === percentile);
    if (!row || row.score_value === null || row.score_value === undefined || row.score_value === '') {
      return '-';
    }
    return String(row.score_value);
  };

  const handleCellClick = async (percentile: number, age: string, scoreValue: string) => {
    if (scoreValue === '-') return;

    if (selectedCell?.percentile === percentile && selectedCell?.age === age) {
      setSelectedCell(null);
      setClassifications([]);
      setConversions([]);
      return;
    }

    const baremo = getBaremoForCell(age, percentile);
    const actualPercentile =
      baremo?.percentile !== undefined && baremo?.percentile !== null ? Number(baremo.percentile) : percentile;
    setSelectedCell({ percentile, age, scoreValue, rowId: baremo?.id });

    try {
      const classificationsResult = await evaluationService.getClassificationsByPercentile(actualPercentile);
      setClassifications(classificationsResult);

      const conversionsResult = await evaluationService.getConversionsByPercentile(actualPercentile);
      setConversions(conversionsResult);
    } catch (error) {
      console.error('Error obteniendo conversiones D2:', error);
      setConversions([]);
      setClassifications([]);
    }
  };

  const handleTableContainerClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setSelectedCell(null);
      setClassifications([]);
      setConversions([]);
    }
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ mb: 2 }} onClick={handleTableContainerClick}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200'),
                  textAlign: 'center',
                }}
              >
                Percentil
              </TableCell>
              {ageGroups.map((age) => {
                const isHoveredColumn = hoveredCell?.age === age;
                const isSelectedColumn = selectedCell?.age === age;
                return (
                  <TableCell
                    key={age}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: isSelectedColumn
                        ? 'rgba(76, 175, 80, 0.25)'
                        : isHoveredColumn
                          ? 'rgba(25, 118, 210, 0.15)'
                          : (theme) => (theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200'),
                      textAlign: 'center',
                      minWidth: 80,
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    {age}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {percentiles.map((percentile) => {
              const isHoveredRow = hoveredCell?.percentile === percentile;
              const isSelectedRow = selectedCell?.percentile === percentile;
              return (
                <TableRow
                  key={percentile}
                  sx={{
                    backgroundColor: isSelectedRow
                      ? 'rgba(76, 175, 80, 0.08)'
                      : isHoveredRow
                        ? 'rgba(25, 118, 210, 0.08)'
                        : 'transparent',
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      textAlign: 'center',
                      backgroundColor: isSelectedRow
                        ? 'rgba(76, 175, 80, 0.25)'
                        : isHoveredRow
                          ? 'rgba(25, 118, 210, 0.15)'
                          : (theme) => (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50'),
                    }}
                  >
                    {percentile}
                  </TableCell>
                  {ageGroups.map((age) => {
                    const scoreValue = getScoreValue(age, percentile);
                    const hasValue = scoreValue !== '-';
                    const isHovered = hasValue && (hoveredCell?.percentile === percentile || hoveredCell?.age === age);
                    const isSelected = hasValue && (selectedCell?.percentile === percentile || selectedCell?.age === age);
                    const isExactSelected = hasValue && selectedCell?.percentile === percentile && selectedCell?.age === age;
                    const isExactHovered = hasValue && hoveredCell?.percentile === percentile && hoveredCell?.age === age;

                    let backgroundColor = 'transparent';
                    if (isExactSelected) backgroundColor = 'rgba(76, 175, 80, 0.4)';
                    else if (isExactHovered) backgroundColor = 'rgba(25, 118, 210, 0.25)';
                    else if (isSelected && isHovered) backgroundColor = 'rgba(76, 175, 80, 0.2)';
                    else if (isSelected) backgroundColor = 'rgba(76, 175, 80, 0.12)';
                    else if (isHovered) backgroundColor = 'rgba(25, 118, 210, 0.12)';

                    return (
                      <TableCell
                        key={`${age}-${percentile}`}
                        onClick={() => hasValue && handleCellClick(percentile, age, scoreValue)}
                        onMouseEnter={() => hasValue && setHoveredCell({ percentile, age, scoreValue })}
                        onMouseLeave={() => setHoveredCell(null)}
                        sx={{
                          textAlign: 'center',
                          fontWeight: hasValue ? 'bold' : 'normal',
                          color: hasValue ? 'text.primary' : 'text.secondary',
                          backgroundColor,
                          cursor: hasValue ? 'pointer' : 'default',
                          transition: 'background-color 0.15s ease',
                          border: isExactSelected ? '2px solid' : 'none',
                          borderColor: isExactSelected ? 'success.main' : 'transparent',
                        }}
                      >
                        {scoreValue}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <EvaluationSelectionPanel
        key={selectedCell ? `${selectedCell.age}-${selectedCell.percentile}` : 'none'}
        selection={
          selectedCell
            ? {
                label: `Edad ${selectedCell.age}`,
                scoreValue: selectedCell.scoreValue ?? null,
                units: '',
                percentile: selectedCell.percentile,
                classifications,
                conversions,
              }
            : null
        }
        editable={isAdmin && !!tableKey && !!selectedCell?.rowId}
        editableFields={
          selectedCell?.rowId
            ? [
                { key: 'percentile', label: 'Percentil', value: selectedCell.percentile, type: 'number' },
                { key: 'score_value', label: 'Puntaje / rango', value: selectedCell.scoreValue ?? null, type: 'text' },
              ]
            : undefined
        }
        onSaveEdit={
          selectedCell?.rowId && tableKey
            ? async (values) => {
                await adminService.updateNormRow(tableKey, selectedCell.rowId as number, values);
                setSelectedCell(null);
                setClassifications([]);
                setConversions([]);
                onRefresh?.();
              }
            : undefined
        }
      />
    </>
  );
};

const D2AgeBaremosTable: React.FC<{ conditions: D2Condition[]; ageGroup: string }> = ({ conditions, ageGroup }) => {
  const [hoveredCell, setHoveredCell] = useState<{ percentile: number; metric: string; scoreValue?: string | null } | null>(null);
  const [selectedCell, setSelectedCell] = useState<{ percentile: number; metric: string; scoreValue?: string | null } | null>(null);
  const [classifications, setClassifications] = useState<string[]>([]);
  const [conversions, setConversions] = useState<any[]>([]);

  const allPercentiles = new Set<number>();

  conditions.forEach((condition) => {
    const rows = condition.baremos?.[ageGroup] || [];
    rows.forEach((row) => {
      if (row.percentile !== null && row.percentile !== undefined) {
        allPercentiles.add(Number(row.percentile));
      }
    });
  });

  const percentiles = Array.from(allPercentiles).sort((a, b) => b - a);

  const getScoreValue = (condition: D2Condition, percentile: number): string => {
    const rows = condition.baremos?.[ageGroup] || [];
    const row = rows.find((r) => Number(r.percentile) === percentile);
    if (!row || row.score_value === null || row.score_value === undefined || row.score_value === '') {
      return '-';
    }
    return String(row.score_value);
  };

  const handleCellClick = async (percentile: number, metric: string, scoreValue: string) => {
    if (scoreValue === '-') return;

    if (selectedCell?.percentile === percentile && selectedCell?.metric === metric) {
      setSelectedCell(null);
      setClassifications([]);
      setConversions([]);
      return;
    }

    setSelectedCell({ percentile, metric, scoreValue });
    try {
      const classificationsResult = await evaluationService.getClassificationsByPercentile(percentile);
      setClassifications(classificationsResult);

      const conversionsResult = await evaluationService.getConversionsByPercentile(percentile);
      setConversions(conversionsResult);
    } catch (error) {
      console.error('Error obteniendo conversiones D2:', error);
      setConversions([]);
      setClassifications([]);
    }
  };

  const handleTableContainerClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setSelectedCell(null);
      setClassifications([]);
      setConversions([]);
    }
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ mb: 2 }} onClick={handleTableContainerClick}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200'),
                  textAlign: 'center',
                }}
              >
                Percentil
              </TableCell>
              {conditions.map((condition) => {
                const isHoveredColumn = hoveredCell?.metric === condition.id;
                const isSelectedColumn = selectedCell?.metric === condition.id;
                return (
                  <TableCell
                    key={condition.id}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: isSelectedColumn
                        ? 'rgba(76, 175, 80, 0.25)'
                        : isHoveredColumn
                          ? 'rgba(25, 118, 210, 0.15)'
                          : (theme) => (theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200'),
                      textAlign: 'center',
                      minWidth: 70,
                    }}
                  >
                    {condition.name}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {percentiles.map((percentile) => {
              const isHoveredRow = hoveredCell?.percentile === percentile;
              const isSelectedRow = selectedCell?.percentile === percentile;
              return (
                <TableRow
                  key={percentile}
                  sx={{
                    backgroundColor: isSelectedRow
                      ? 'rgba(76, 175, 80, 0.08)'
                      : isHoveredRow
                        ? 'rgba(25, 118, 210, 0.08)'
                        : 'transparent',
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      textAlign: 'center',
                      backgroundColor: isSelectedRow
                        ? 'rgba(76, 175, 80, 0.25)'
                        : isHoveredRow
                          ? 'rgba(25, 118, 210, 0.15)'
                          : (theme) => (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50'),
                    }}
                  >
                    {percentile}
                  </TableCell>
                  {conditions.map((condition) => {
                    const scoreValue = getScoreValue(condition, percentile);
                    const hasValue = scoreValue !== '-';
                    const isHovered = hasValue && (hoveredCell?.percentile === percentile || hoveredCell?.metric === condition.id);
                    const isSelected = hasValue && (selectedCell?.percentile === percentile || selectedCell?.metric === condition.id);
                    const isExactSelected = hasValue && selectedCell?.percentile === percentile && selectedCell?.metric === condition.id;
                    const isExactHovered = hasValue && hoveredCell?.percentile === percentile && hoveredCell?.metric === condition.id;

                    let backgroundColor = 'transparent';
                    if (isExactSelected) backgroundColor = 'rgba(76, 175, 80, 0.4)';
                    else if (isExactHovered) backgroundColor = 'rgba(25, 118, 210, 0.25)';
                    else if (isSelected && isHovered) backgroundColor = 'rgba(76, 175, 80, 0.2)';
                    else if (isSelected) backgroundColor = 'rgba(76, 175, 80, 0.12)';
                    else if (isHovered) backgroundColor = 'rgba(25, 118, 210, 0.12)';

                    return (
                      <TableCell
                        key={`${condition.id}-${percentile}`}
                        onClick={() => hasValue && handleCellClick(percentile, condition.id, scoreValue)}
                        onMouseEnter={() => hasValue && setHoveredCell({ percentile, metric: condition.id, scoreValue })}
                        onMouseLeave={() => setHoveredCell(null)}
                        sx={{
                          textAlign: 'center',
                          fontWeight: hasValue ? 'bold' : 'normal',
                          color: hasValue ? 'text.primary' : 'text.secondary',
                          backgroundColor,
                          cursor: hasValue ? 'pointer' : 'default',
                          border: isExactSelected ? '2px solid' : 'none',
                          borderColor: isExactSelected ? 'success.main' : 'transparent',
                        }}
                      >
                        {scoreValue}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <EvaluationSelectionPanel
        selection={
          selectedCell
            ? {
                label: `Métrica ${selectedCell.metric}`,
                scoreValue: selectedCell.scoreValue ?? null,
                units: '',
                percentile: selectedCell.percentile,
                classifications,
                conversions,
              }
            : null
        }
      />
    </>
  );
};

interface MocaBaremosTableProps {
  baremosData: Record<string, BaremosData[]>;
  isAdmin?: boolean;
  onRefresh?: () => void;
}

interface MocaCellPosition {
  ageGroup: string;
  percentileMin: number;
  percentileMax: number;
  scoreLabel: string;
  rowId?: number;
  scoreMin?: number;
  scoreMax?: number;
}

const MocaBaremosTable: React.FC<MocaBaremosTableProps> = ({ baremosData, isAdmin = false, onRefresh }) => {
  const [hoveredCell, setHoveredCell] = useState<MocaCellPosition | null>(null);
  const [selectedCell, setSelectedCell] = useState<MocaCellPosition | null>(null);
  const [classifications, setClassifications] = useState<string[]>([]);
  const [conversions, setConversions] = useState<any[]>([]);

  const ageGroups = Object.keys(baremosData).sort((a, b) => a.localeCompare(b, 'es'));

  // Filas únicas por rango de percentil (percentile_min/percentile_max), ordenadas de mayor a menor
  const percentileRowsMap = new Map<string, { min: number; max: number }>();
  Object.values(baremosData).forEach((rows) => {
    rows.forEach((row) => {
      if (row.percentile_min !== undefined && row.percentile_max !== undefined) {
        const key = `${row.percentile_min}-${row.percentile_max}`;
        percentileRowsMap.set(key, { min: row.percentile_min, max: row.percentile_max });
      }
    });
  });
  const percentileRows = Array.from(percentileRowsMap.values()).sort((a, b) => b.min - a.min);

  const formatPercentile = (min: number, max: number) => (min === max ? `${min}` : `${min}-${max}`);
  const formatScore = (row?: BaremosData) => {
    if (!row || row.score_min === undefined || row.score_max === undefined) return '-';
    return row.score_min === row.score_max ? `${row.score_min}` : `${row.score_min}-${row.score_max}`;
  };
  const getCellRow = (ageGroup: string, min: number, max: number) =>
    (baremosData[ageGroup] || []).find((r) => r.percentile_min === min && r.percentile_max === max);

  const headerBg = (theme: any) => (theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200');

  // Un rango de percentil (min/max) puede caer entre dos clasificaciones distintas:
  // se consultan ambos extremos y se unen/deduplican, igual que en el cálculo de MoCA.
  const handleCellClick = async (ageGroup: string, min: number, max: number, scoreLabel: string) => {
    if (
      selectedCell?.ageGroup === ageGroup &&
      selectedCell?.percentileMin === min &&
      selectedCell?.percentileMax === max
    ) {
      setSelectedCell(null);
      setClassifications([]);
      setConversions([]);
      return;
    }

    const row = getCellRow(ageGroup, min, max);
    setSelectedCell({
      ageGroup,
      percentileMin: min,
      percentileMax: max,
      scoreLabel,
      rowId: row?.id,
      scoreMin: row?.score_min,
      scoreMax: row?.score_max,
    });

    try {
      const [clsMin, clsMax] = await Promise.all([
        evaluationService.getClassificationsByPercentile(min),
        evaluationService.getClassificationsByPercentile(max),
      ]);
      setClassifications(Array.from(new Set([...clsMin, ...clsMax])));

      const [convMin, convMax] = await Promise.all([
        evaluationService.getConversionsByPercentile(min),
        evaluationService.getConversionsByPercentile(max),
      ]);
      const seen = new Set<string>();
      const mergedConversions = [...convMin, ...convMax].filter((c) => {
        const key = c.classification ?? JSON.stringify(c);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setConversions(mergedConversions);
    } catch (error) {
      console.error('Error obteniendo conversiones MoCA:', error);
      setClassifications([]);
      setConversions([]);
    }
  };

  const handleTableContainerClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setSelectedCell(null);
      setClassifications([]);
      setConversions([]);
    }
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ mb: 2 }} onClick={handleTableContainerClick}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: headerBg }}>Percentil</TableCell>
              {ageGroups.map((age) => (
                <TableCell key={age} align="center" sx={{ fontWeight: 'bold', backgroundColor: headerBg }}>
                  {age} años
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {percentileRows.map(({ min, max }) => (
              <TableRow key={`${min}-${max}`}>
                <TableCell sx={{ fontWeight: 'bold' }}>{formatPercentile(min, max)}</TableCell>
                {ageGroups.map((age) => {
                  const row = getCellRow(age, min, max);
                  const scoreLabel = formatScore(row);
                  const hasValue = scoreLabel !== '-';
                  const isExactSelected =
                    selectedCell?.ageGroup === age && selectedCell?.percentileMin === min && selectedCell?.percentileMax === max;
                  const isExactHovered =
                    hoveredCell?.ageGroup === age && hoveredCell?.percentileMin === min && hoveredCell?.percentileMax === max;
                  const isSelectedRow = selectedCell?.percentileMin === min && selectedCell?.percentileMax === max;
                  const isHoveredRow = hoveredCell?.percentileMin === min && hoveredCell?.percentileMax === max;
                  const isSelectedCol = selectedCell?.ageGroup === age;
                  const isHoveredCol = hoveredCell?.ageGroup === age;

                  let backgroundColor = 'transparent';
                  if (isExactSelected) backgroundColor = 'rgba(76, 175, 80, 0.4)';
                  else if (isExactHovered) backgroundColor = 'rgba(25, 118, 210, 0.25)';
                  else if ((isSelectedRow || isSelectedCol) && (isHoveredRow || isHoveredCol)) backgroundColor = 'rgba(76, 175, 80, 0.2)';
                  else if (isSelectedRow || isSelectedCol) backgroundColor = 'rgba(76, 175, 80, 0.12)';
                  else if (isHoveredRow || isHoveredCol) backgroundColor = 'rgba(25, 118, 210, 0.12)';

                  return (
                    <TableCell
                      key={age}
                      align="center"
                      onClick={() => hasValue && handleCellClick(age, min, max, scoreLabel)}
                      onMouseEnter={() => hasValue && setHoveredCell({ ageGroup: age, percentileMin: min, percentileMax: max, scoreLabel })}
                      onMouseLeave={() => setHoveredCell(null)}
                      sx={{
                        fontWeight: hasValue ? 'bold' : 'normal',
                        color: hasValue ? 'text.primary' : 'text.secondary',
                        backgroundColor,
                        cursor: hasValue ? 'pointer' : 'default',
                        transition: 'background-color 0.15s ease',
                        border: isExactSelected ? '2px solid' : 'none',
                        borderColor: isExactSelected ? 'success.main' : 'transparent',
                      }}
                    >
                      {scoreLabel}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <EvaluationSelectionPanel
        key={selectedCell ? `${selectedCell.ageGroup}-${selectedCell.percentileMin}-${selectedCell.percentileMax}` : 'none'}
        selection={
          selectedCell
            ? {
                label: `Edad ${selectedCell.ageGroup}`,
                scoreValue: selectedCell.scoreLabel,
                units: 'puntos',
                percentile: selectedCell.percentileMin === selectedCell.percentileMax
                  ? selectedCell.percentileMin
                  : `${selectedCell.percentileMin}-${selectedCell.percentileMax}`,
                classifications,
                conversions,
              }
            : null
        }
        editable={isAdmin && !!selectedCell?.rowId}
        editableFields={
          selectedCell?.rowId
            ? [
                { key: 'score_min', label: 'Puntaje mín.', value: selectedCell.scoreMin ?? null, type: 'number' },
                { key: 'score_max', label: 'Puntaje máx.', value: selectedCell.scoreMax ?? null, type: 'number' },
                { key: 'percentile_min', label: 'Percentil mín.', value: selectedCell.percentileMin, type: 'number' },
                { key: 'percentile_max', label: 'Percentil máx.', value: selectedCell.percentileMax, type: 'number' },
              ]
            : undefined
        }
        onSaveEdit={
          selectedCell?.rowId
            ? async (values) => {
                await adminService.updateNormRow('moca_norms', selectedCell.rowId as number, values);
                setSelectedCell(null);
                setClassifications([]);
                setConversions([]);
                onRefresh?.();
              }
            : undefined
        }
      />

      <Box sx={{ mt: 3, p: 2, backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.15)' : 'info.50', borderRadius: 1 }}>
        <Typography variant="body2" color="info.main">
          <strong>Nota:</strong> Los valores representan el rango de puntaje directo total de MoCA (0-30) que corresponde a cada rango de percentil, por grupo etario.
          {' '}<strong>Tip:</strong> Haz clic en una celda para mantener la selección resaltada.
        </Typography>
      </Box>
    </>
  );
};

interface IfsChBaremosTableProps {
  rows: IFSChNormRow[];
  isAdmin?: boolean;
  onRefresh?: () => void;
}

// Postgres devuelve DECIMAL(5,2) como "2.80"; la tabla original solo tiene 1 decimal.
const formatIfsChValue = (value: number | string) => String(Number(value));

// IFS-Ch no tiene percentiles ni grupos etarios/educacionales: es una tabla de
// referencia plana (Media ± DE de sujetos control por subtest), por lo que no usa
// el patrón de click→EvaluationSelectionPanel de los demás baremos. La edición
// admin igual aplica (regla general), pero como edición inline por fila.
const IfsChBaremosTable: React.FC<IfsChBaremosTableProps> = ({ rows, isAdmin = false, onRefresh }) => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editMean, setEditMean] = useState('');
  const [editSd, setEditSd] = useState('');
  const [saving, setSaving] = useState(false);

  const startEdit = (row: IFSChNormRow) => {
    setEditingId(row.id);
    setEditMean(String(row.mean_value));
    setEditSd(String(row.sd_value));
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (id: number) => {
    setSaving(true);
    try {
      await adminService.updateNormRow('ifs_ch_norms', id, {
        mean_value: parseFloat(editMean),
        sd_value: parseFloat(editSd),
      });
      setEditingId(null);
      onRefresh?.();
    } catch (error) {
      console.error('Error actualizando IFS-Ch:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Subtest</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Media (Control Subjects)</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>DE</TableCell>
              {isAdmin && <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const isTotal = row.subtest_en === 'Total IFS-Ch';
              const isEditing = editingId === row.id;
              return (
                <TableRow
                  key={row.id}
                  sx={isTotal ? {
                    '& td': { fontWeight: 'bold' },
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                  } : undefined}
                >
                  <TableCell>
                    {row.subtest_es}{' '}
                    <Typography component="span" variant="body2" color="text.secondary">
                      ({row.subtest_en})
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {isEditing ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editMean}
                        onChange={(e) => setEditMean(e.target.value)}
                        sx={{ width: 90 }}
                      />
                    ) : formatIfsChValue(row.mean_value)}
                  </TableCell>
                  <TableCell align="center">
                    {isEditing ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editSd}
                        onChange={(e) => setEditSd(e.target.value)}
                        sx={{ width: 90 }}
                      />
                    ) : formatIfsChValue(row.sd_value)}
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="center">
                      {isEditing ? (
                        <>
                          <IconButton size="small" color="success" onClick={() => saveEdit(row.id)} disabled={saving}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={cancelEdit} disabled={saving}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <IconButton size="small" onClick={() => startEdit(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3, p: 2, backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.15)' : 'info.50', borderRadius: 1 }}>
        <Typography variant="body2" color="info.main">
          <strong>Nota:</strong> Los valores corresponden a la Media ± Desviación Estándar del grupo de sujetos control (n=30). Es una tabla de referencia, no percentilar por edad/educación.
        </Typography>
      </Box>
    </>
  );
};

const EvaluationDetailPage: React.FC = () => {
  const { evaluationId } = useParams<{ evaluationId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [baremosData, setBaremosData] = useState<Record<string, BaremosData[]>>({});
  const [ifsChRows, setIfsChRows] = useState<IFSChNormRow[]>([]);
  const [stroopConditions, setStroopConditions] = useState<(StroopCondition | FluenciasCondition | HVLTRCondition | ReyFigureCondition | D2Condition | ElevatorCondition)[]>([]);
  const [evaluationInfo, setEvaluationInfo] = useState<ServiceEvaluationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [d2ViewMode, setD2ViewMode] = useState<D2ViewMode>('metric');

  const evaluation = evaluationId ? evaluations[evaluationId] : null;
  const isStroop = evaluationId === 'stroop';
  const isFluencias = evaluationId === 'fluencias';
  const isHVLTR = evaluationId === 'hvlt-r';
  const isReyFigure = evaluationId === 'rey-figure';
  const isD2 = evaluationId === 'd2';
  const isElevator3B = evaluationId === 'elevator-appendix-3b';
  const isMoca = evaluationId === 'moca';
  const isBNT = evaluationId === 'boston-naming';
  const isIFSCh = evaluationId === 'ifs-ch';

  useEffect(() => {
    if (!evaluation) {
      navigate('/evaluations');
      return;
    }

    loadBaremos();
  }, [evaluationId, evaluation, navigate]);

  const loadBaremos = async () => {
    if (!evaluationId) return;

    setLoading(true);
    setError(null);

    try {
      if (evaluationId === 'tmt-a') {
        const response = await evaluationService.getTMTABaremos();
        if (response.success) {
          setBaremosData(response.data.baremos);
          setEvaluationInfo(response.data.evaluation);
        }
      } else if (evaluationId === 'tmt-b') {
        const response = await evaluationService.getTMTBBaremos();
        if (response.success) {
          setBaremosData(response.data.baremos);
          setEvaluationInfo(response.data.evaluation);
        }
      } else if (evaluationId === 'stroop') {
        const response = await evaluationService.getStroopBaremos();
        if (response.success) {
          setStroopConditions(response.data.conditions);
          setEvaluationInfo(response.data.evaluation);
        }
      } else if (evaluationId === 'fluencias') {
        const response = await evaluationService.getFluenciasBaremos();
        if (response.success) {
          setStroopConditions(response.data.conditions);
          setEvaluationInfo(response.data.evaluation);
        }
      } else if (evaluationId === 'hvlt-r') {
        const response = await evaluationService.getHVLTRBaremos();
        if (response.success) {
          setStroopConditions(response.data.conditions);
          setEvaluationInfo(response.data.evaluation);
        }
      } else if (evaluationId === 'rey-figure') {
        const response = await evaluationService.getReyFigureBaremos();
        if (response.success) {
          setStroopConditions(response.data.conditions);
          setEvaluationInfo(response.data.evaluation);
        }
      } else if (evaluationId === 'd2') {
        const response = await evaluationService.getD2Baremos();
        if (response.success) {
          setStroopConditions(response.data.conditions);
          setEvaluationInfo(response.data.evaluation);
        }
      } else if (evaluationId === 'elevator-appendix-3b') {
        const response = await evaluationService.getElevatorAppendix3BBaremos();
        if (response.success) {
          setStroopConditions(response.data.conditions);
          setEvaluationInfo(response.data.evaluation);
        }
      } else if (evaluationId === 'moca') {
        const response = await evaluationService.getMOCABaremos();
        if (response.success) {
          setBaremosData(response.data.baremos);
          setEvaluationInfo(response.data.evaluation);
        }
      } else if (evaluationId === 'boston-naming') {
        const response = await evaluationService.getBNTBaremos();
        if (response.success) {
          setBaremosData(response.data.baremos);
          setEvaluationInfo(response.data.evaluation);
        }
      } else if (evaluationId === 'ifs-ch') {
        const response = await evaluationService.getIFSChBaremos();
        if (response.success) {
          setIfsChRows(response.data.baremos);
          setEvaluationInfo(response.data.evaluation);
        }
      } else {
        throw new Error('Evaluación no soportada');
      }
    } catch (err) {
      console.error('Error cargando baremos:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  if (!evaluation) {
    return null;
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleD2ViewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: D2ViewMode | null) => {
    if (!newMode) return;
    setD2ViewMode(newMode);
    setSelectedTab(0);
  };

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 3 }}
      >
        <Link
          color="inherit"
          component={RouterLink}
          to="/evaluations"
          sx={{ cursor: 'pointer' }}
        >
          Evaluaciones
        </Link>
        <Typography color="text.primary">{evaluation.name}</Typography>
      </Breadcrumbs>

      {/* Header de la evaluación */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ color: 'primary.main', mr: 2 }}>
            {evaluation.icon}
          </Box>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {evaluation.fullName}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {evaluation.description}
            </Typography>
          </Box>
        </Box>
        
        <Chip 
          label={evaluation.category} 
          color="primary" 
          variant="outlined"
        />
      </Box>

      {/* Loading */}
      {loading && <LoadingOrb size={64} minHeight="120px" />}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Contenido según el tipo de evaluación */}
      {!loading && !error && evaluationInfo && (
        <Card>
          <CardContent>
            {isStroop || isFluencias || isHVLTR ? (
              // Vista para Stroop con Tabs para las 4 condiciones
              <>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  {`Datos normativos para ${evaluationInfo.fullName} estratificados por edad y nivel educativo para CHILE`}
                </Typography>
                
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                  <Tabs 
                    value={selectedTab} 
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {stroopConditions.map((condition, index) => (
                      <Tab 
                        key={condition.id} 
                        label={condition.name}
                        id={`stroop-tab-${index}`}
                      />
                    ))}
                  </Tabs>
                </Box>

                {stroopConditions.map((condition, index) => (
                  <Box
                    key={condition.id}
                    role="tabpanel"
                    hidden={selectedTab !== index}
                    id={`stroop-tabpanel-${index}`}
                  >
                    {selectedTab === index && (
                      <Box>
                        <Box sx={{ mb: 3, p: 2, backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50', borderRadius: 1 }}>
                          <Typography variant="h6" gutterBottom>
                            {condition.fullName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {condition.description}
                          </Typography>
                          <Chip 
                            label={`Unidad: ${condition.units}`} 
                            size="small" 
                            sx={{ mt: 1 }}
                          />
                        </Box>
                        
                        <BaremosTable
                          baremosData={condition.baremos}
                          units={condition.units}
                          canBeNegative={condition.id === 'interferencia'}
                          tableKey={condition.table}
                          isAdmin={isAdmin}
                          onRefresh={loadBaremos}
                        />
                      </Box>
                    )}
                  </Box>
                ))}
              </>
            ) : isElevator3B ? (
              <>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Datos normativos para {evaluationInfo.fullName} estratificados por edad y versión (A/B)
                </Typography>

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                  <Tabs value={selectedTab} onChange={handleTabChange}>
                    {stroopConditions.map((condition, index) => (
                      <Tab
                        key={condition.id}
                        label={condition.name}
                        id={`elevator-3b-tab-${index}`}
                        aria-controls={`elevator-3b-tabpanel-${index}`}
                      />
                    ))}
                  </Tabs>
                </Box>

                {stroopConditions.map((condition, index) => (
                  <Box
                    key={condition.id}
                    role="tabpanel"
                    hidden={selectedTab !== index}
                    id={`elevator-3b-tabpanel-${index}`}
                  >
                    {selectedTab === index && (
                      <Box>
                        <Box sx={{ mb: 3, p: 2, backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50', borderRadius: 1 }}>
                          <Typography variant="h6" gutterBottom>
                            {condition.fullName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {condition.description}
                          </Typography>
                          <Chip label={`Unidad: ${condition.units}`} size="small" sx={{ mt: 1 }} />
                        </Box>

                        <ReyFigureBaremosTable baremosData={condition.baremos} units={condition.units} tableKey={condition.table} isAdmin={isAdmin} onRefresh={loadBaremos} />
                        <ElevatorReferenceGuide />
                      </Box>
                    )}
                  </Box>
                ))}
              </>
            ) : isReyFigure ? (
              // Vista para Rey Figure (similar a Stroop/Fluencias/HVLT-R)
              <>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                  <Tabs value={selectedTab} onChange={handleTabChange}>
                    {stroopConditions.map((condition, index) => (
                      <Tab
                        key={condition.id}
                        label={condition.name}
                        id={`rey-figure-tab-${index}`}
                        aria-controls={`rey-figure-tabpanel-${index}`}
                      />
                    ))}
                  </Tabs>
                </Box>

                {stroopConditions.map((condition, index) => (
                  <Box
                    key={condition.id}
                    role="tabpanel"
                    hidden={selectedTab !== index}
                    id={`rey-figure-tabpanel-${index}`}
                  >
                    {selectedTab === index && (
                      <Box>
                        <Box sx={{ mb: 3, p: 2, backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50', borderRadius: 1 }}>
                          <Typography variant="h6" gutterBottom>
                            {condition.fullName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {condition.description}
                          </Typography>
                          <Chip 
                            label={`Unidad: ${condition.units}`} 
                            size="small" 
                            sx={{ mt: 1 }}
                          />
                        </Box>
                        
                        <ReyFigureBaremosTable
                          baremosData={condition.baremos}
                          units={condition.units}
                          isStringValue={condition.id === 'memoriaTipoConstruccion'}
                          tableKey={condition.table}
                          isAdmin={isAdmin}
                          onRefresh={loadBaremos}
                        />
                      </Box>
                    )}
                  </Box>
                ))}
              </>
            ) : isD2 ? (
              <>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Baremos D2
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <ToggleButtonGroup
                    value={d2ViewMode}
                    exclusive
                    onChange={handleD2ViewModeChange}
                    size="small"
                  >
                    <ToggleButton value="metric">Vista por métrica</ToggleButton>
                    <ToggleButton value="age">Vista por edad</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                  {d2ViewMode === 'metric' ? (
                    <Tabs value={selectedTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                      {stroopConditions.map((condition, index) => (
                        <Tab key={condition.id} label={condition.name} id={`d2-metric-tab-${index}`} />
                      ))}
                    </Tabs>
                  ) : (
                    <Tabs value={selectedTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                      {Object.keys((stroopConditions[0] as D2Condition | undefined)?.baremos || {})
                        .sort((a, b) => a.localeCompare(b, 'es'))
                        .map((ageGroup, index) => (
                          <Tab key={ageGroup} label={ageGroup} id={`d2-age-tab-${index}`} />
                        ))}
                    </Tabs>
                  )}
                </Box>

                {d2ViewMode === 'metric' ? (
                  stroopConditions.map((condition, index) => (
                    <Box key={condition.id} role="tabpanel" hidden={selectedTab !== index} id={`d2-metric-tabpanel-${index}`}>
                      {selectedTab === index && (
                        <Box>
                          <Box sx={{ mb: 3, p: 2, backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50', borderRadius: 1 }}>
                            <Typography variant="h6" gutterBottom>
                              {condition.fullName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {condition.description}
                            </Typography>
                            <Chip label={`Unidad: ${condition.units}`} size="small" sx={{ mt: 1 }} />
                          </Box>
                          <D2BaremosTable baremosData={condition.baremos} tableKey={condition.table} isAdmin={isAdmin} onRefresh={loadBaremos} />
                        </Box>
                      )}
                    </Box>
                  ))
                ) : (
                  Object.keys((stroopConditions[0] as D2Condition | undefined)?.baremos || {})
                    .sort((a, b) => a.localeCompare(b, 'es'))
                    .map((ageGroup, index) => (
                      <Box key={ageGroup} role="tabpanel" hidden={selectedTab !== index} id={`d2-age-tabpanel-${index}`}>
                        {selectedTab === index && (
                          <Box>
                            <Box sx={{ mb: 3, p: 2, backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50', borderRadius: 1 }}>
                              <Typography variant="h6" gutterBottom>
                                Tramo etario {ageGroup}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Tabla D2 original por percentil y métricas para el tramo {ageGroup}.
                              </Typography>
                              <Chip label="Unidad: rango/valor" size="small" sx={{ mt: 1 }} />
                            </Box>
                            <D2AgeBaremosTable conditions={stroopConditions as D2Condition[]} ageGroup={ageGroup} />
                          </Box>
                        )}
                      </Box>
                    ))
                )}
              </>
            ) : isMoca ? (
              <>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Datos normativos para {evaluationInfo.fullName} (puntaje directo total) estratificados por edad
                </Typography>

                <MocaBaremosTable baremosData={baremosData} isAdmin={isAdmin} onRefresh={loadBaremos} />
              </>
            ) : isBNT ? (
              <>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Datos normativos para {evaluationInfo.fullName} estratificados por edad y nivel educacional para CHILE
                </Typography>

                <BaremosTable
                  baremosData={baremosData}
                  units={evaluationInfo.units || 'puntos'}
                  tableKey="bnt_norms"
                  scoreFieldKey="score"
                  isAdmin={isAdmin}
                  onRefresh={loadBaremos}
                />
              </>
            ) : isIFSCh ? (
              <>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Datos normativos para {evaluationInfo.fullName}: Media ± DE del grupo de sujetos control (n=30) por subtest
                </Typography>

                <IfsChBaremosTable rows={ifsChRows} isAdmin={isAdmin} onRefresh={loadBaremos} />
              </>
            ) : (
              // Vista para TMT-A y TMT-B
              <>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Datos normativos para {evaluationInfo.name} estratificados por edad y nivel educativo para CHILE
                </Typography>

                <BaremosTable
                  baremosData={baremosData}
                  units={evaluationInfo.units || 'segundos'}
                  tableKey={evaluationId === 'tmt-b' ? 'tmt_b_norms' : 'tmt_a_norms'}
                  isAdmin={isAdmin}
                  onRefresh={loadBaremos}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default EvaluationDetailPage;
